import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { fetchJson, assetUrl } from "../lib/api";
import { formatDateFR, formatShortDateFR } from "../lib/format";

const useEvents = () => {
  const [data, setData] = useState({ upcoming: [], past: [], loading: true });

  useEffect(() => {
    (async () => {
      try {
        const all = await fetchJson("/events");
        const upcoming = (all || [])
          .filter((e) => ["upcoming", "announced", "live"].includes(e.status))
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        const past = (all || [])
          .filter((e) => e.status === "completed")
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        setData({ upcoming, past, loading: false });
      } catch (_) {
        setData({ upcoming: [], past: [], loading: false });
      }
    })();
  }, []);

  return data;
};

const UpcomingCard = ({ event }) => (
  <Link
    to={`/events/${event.slug}`}
    data-testid={`event-upcoming-${event.slug}`}
    className="group relative overflow-hidden border border-neutral-900 bg-neutral-950 min-h-[360px] md:min-h-[440px] flex flex-col justify-end"
  >
    {event.poster_url && (
      <img
        src={assetUrl(event.poster_url)}
        alt=""
        aria-hidden
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-transform duration-500"
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
    <div className="relative p-6 md:p-10">
      <p className="text-[10px] font-stats uppercase tracking-[0.3em] text-gold">
        {event.edition_number ? `Édition ${String(event.edition_number).padStart(2, "0")}` : "À venir"}
      </p>
      <h3 className="mt-3 font-display font-black text-4xl md:text-6xl text-white leading-[0.9]">
        {event.name}
      </h3>
      <p className="mt-4 font-stats text-xs md:text-sm text-neutral-400 uppercase tracking-widest flex flex-wrap items-center gap-x-3 gap-y-1">
        <span>{formatDateFR(event.date, { withGmtTime: true })}</span>
        <span className="w-px h-3 bg-neutral-700" />
        <span className="inline-flex items-center gap-1">
          <MapPin size={12} className="text-gold" /> {event.city}
        </span>
      </p>
      <div className="mt-6">
        <span className="inline-flex items-center gap-2 text-sm text-white font-display uppercase tracking-widest border-b border-neutral-700 pb-0.5 group-hover:border-gold transition-colors duration-200">
          Voir la fight card <ArrowRight size={14} />
        </span>
      </div>
    </div>
  </Link>
);

const PastRow = ({ event }) => (
  <Link
    to={`/events/${event.slug}`}
    data-testid={`event-past-${event.slug}`}
    className="grid grid-cols-12 items-center py-6 border-b border-neutral-900 hover:bg-neutral-950/60 transition-colors duration-200 group"
  >
    <div className="col-span-3 md:col-span-2 font-stats text-xs md:text-sm text-gold uppercase tracking-widest">
      {formatShortDateFR(event.date)}
    </div>
    <div className="col-span-9 md:col-span-7 font-display font-bold text-white uppercase text-xl md:text-3xl leading-tight">
      {event.name}
    </div>
    <div className="col-span-6 md:col-span-2 font-stats text-xs text-neutral-400 uppercase tracking-widest mt-2 md:mt-0">
      {event.city}
    </div>
    <div className="hidden md:block md:col-span-1 text-right text-neutral-500 group-hover:text-white transition-colors duration-200">
      →
    </div>
  </Link>
);

export default function Events() {
  const { upcoming, past, loading } = useEvents();

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8" data-testid="events-page">
      <div className="pt-14 md:pt-20 pb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
          Calendrier BUFL
        </p>
        <h1 className="mt-4 font-display font-black text-6xl md:text-9xl leading-[0.85] text-white">
          ÉVÉNEMENTS
        </h1>
      </div>

      {/* Upcoming */}
      <section className="py-6 md:py-10 border-t border-neutral-900">
        <div className="flex items-end justify-between">
          <h2 className="font-display font-black text-3xl md:text-5xl text-white">
            PROCHAINS ÉVÉNEMENTS
          </h2>
          <p className="font-stats text-xs uppercase tracking-widest text-neutral-500">
            {upcoming.length} annoncé{upcoming.length > 1 ? "s" : ""}
          </p>
        </div>

        {loading ? (
          <p className="mt-8 text-neutral-600 font-stats text-xs uppercase tracking-widest animate-pulse">
            Chargement…
          </p>
        ) : upcoming.length === 0 ? (
          <div className="mt-8 border border-neutral-900 line-grid py-20 px-8 text-center">
            <p className="font-display font-black text-3xl md:text-5xl text-neutral-600">
              AUCUN ÉVÉNEMENT ANNONCÉ
            </p>
            <p className="mt-3 text-neutral-500 text-sm">
              La prochaine fight night sera bientôt révélée.
            </p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {upcoming.map((e) => (
              <UpcomingCard key={e.id} event={e} />
            ))}
          </div>
        )}
      </section>

      {/* Past */}
      <section className="py-10 md:py-16 border-t border-neutral-900">
        <div className="flex items-end justify-between">
          <h2 className="font-display font-black text-3xl md:text-5xl text-white">
            ÉVÉNEMENTS PASSÉS
          </h2>
        </div>

        {past.length === 0 && !loading ? (
          <div className="mt-8 border border-neutral-900 line-grid py-20 px-8 text-center">
            <p className="font-display font-black text-3xl md:text-5xl text-neutral-600">
              PAS D'HISTORIQUE
            </p>
          </div>
        ) : (
          <div className="mt-8 border-t border-neutral-900">
            {past.map((e) => (
              <PastRow key={e.id} event={e} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
